import { all, call, put, select, takeLatest } from 'redux-saga/effects';
import { getFormValues } from 'redux-form';

import { hideLoader, showLoader } from '@topconagriculture/loader';

import api from '../../../core/api';
import { farmActions, feedingActions, loadMixersActions } from '../../actions';
import { LoadersNames } from '../../ui/ui.constants';
import { composeErrorMessage } from '../../../core/helpers/utils/utils';
import { resetFeedingLoads } from '../../application/loadMixers/loadMixers.actions';
import { notificationError, notificationSuccess } from '../../../core/helpers/utils/notificationUtils';
import { getFeedingPeriodList, getUpdatedFeedingPeriodsForms } from './feeding.selector';
import { compose403ErrorMessage, successHandler } from '../../../core/helpers/utils/sagaUtils';
import { Forms } from '../../form/form.selector';

export function* getFeedingPeriodListSaga() {
  yield put(showLoader(LoadersNames.fetchFeedingPeriods));
  try {
    const result = yield call(api.feeding.fetchFeedingPeriodListWithFeedings);

    yield put(
      feedingActions.getFeedingPeriodList.done({
        params: {},
        result,
      }),
    );
    yield put(loadMixersActions.getAllFeedingPeriodDetail.started({}));
  } catch (err) {
    yield put(notificationError(composeErrorMessage(err)));
  }
  yield put(hideLoader(LoadersNames.fetchFeedingPeriods));
}

export function* updateFeedingPeriodSaga(action: ReturnType<typeof feedingActions.updateFeedingPeriod.started>) {
  yield put(showLoader(LoadersNames.updateFeedingPeriod));
  try {
    const formName = action.payload;
    const forms = yield select(getUpdatedFeedingPeriodsForms);
    let formValues = yield all(
      forms.map((form) => {
        const values = select(getFormValues(form));
        return values;
      }),
    );
    formValues = formValues.map((values) => ({
      id: values.id,
      description: values.description,
      displayColor: values.displayColor,
      timeMonday: values.timeMonday === '' ? undefined : values.timeMonday,
      timeTuesday: values.timeTuesday === '' ? undefined : values.timeTuesday,
      timeWednesday: values.timeWednesday === '' ? undefined : values.timeWednesday,
      timeThursday: values.timeThursday === '' ? undefined : values.timeThursday,
      timeFriday: values.timeFriday === '' ? undefined : values.timeFriday,
      timeSaturday: values.timeSaturday === '' ? undefined : values.timeSaturday,
      timeSunday: values.timeSunday === '' ? undefined : values.timeSunday,
    }));
    const results = yield all(
      formValues.map((values) => {
        const { id, ...data } = values;
        return call(api.feeding.updateFeedingPeriod, id, data, formName);
      }),
    );
    yield all(
      results.map((result) => {
        return put(feedingActions.updateFeedingPeriod.done({ params: action.payload, result }));
      }),
    );
    yield put(notificationSuccess('settings:update_success'));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
  yield put(hideLoader(LoadersNames.updateFeedingPeriod));
}

export function* createFeeingPeriodSaga(action: ReturnType<typeof feedingActions.createFeedingPeriod.started>) {
  const { formName } = action.payload;
  const formValues = yield select(getFormValues(Forms.createFeedingPeriod));
  const feedingPeriods = yield select(getFeedingPeriodList);
  const { description, displayColor } = formValues;
  const feedingPeriodsNumber = [...feedingPeriods].pop().feedingNo + 1;
  try {
    const result = yield call(
      api.feeding.createFeedingPeriod,
      { feedingNo: feedingPeriodsNumber, description, displayColor },
      formName,
    );
    yield put(feedingActions.createFeedingPeriod.done({ result, params: { formName } }));
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* deleteFeedingPeriodSaga(action: ReturnType<typeof feedingActions.deleteFeedingPeriod.started>) {
  const { id, formName } = action.payload;

  try {
    yield call(api.feeding.deleteFeedingPeriod, id, formName);
    yield put(
      feedingActions.deleteFeedingPeriod.done({
        params: action.payload,
        result: id,
      }),
    );
    successHandler(action.meta);
  } catch (err) {
    yield compose403ErrorMessage(err);
  }
}

export function* refetchFeedingsSaga() {
  yield put(resetFeedingLoads());
  yield put(feedingActions.getFeedingPeriodList.started({}));
}

export function* watchFeedingSaga() {
  yield all([
    takeLatest(feedingActions.getFeedingPeriodList.started, getFeedingPeriodListSaga),
    takeLatest(farmActions.getFarmsListRequest.done, getFeedingPeriodListSaga),
    takeLatest(feedingActions.updateFeedingPeriod.started, updateFeedingPeriodSaga),
    takeLatest(feedingActions.createFeedingPeriod.started, createFeeingPeriodSaga),
    takeLatest(feedingActions.deleteFeedingPeriod.started, deleteFeedingPeriodSaga),
  ]);
}
